import { Button, Drawer, Form, TextField } from '@boltenergy-be/design-system'
import {
  FixedPriceModalProps,
  SalesPricesFormInputs
} from 'features/registration/components/registration-side-card/sales-prices-section/types'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import styles from '../price-simulation-section/edit-instalment-drawer/EditInstalmentDrawer.module.scss'
import { updateFixedPrices, updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { InputGroupKeys, SimulationFieldKeys } from 'store/customer/enums'
import { SIMULATION_TYPES } from '@boltenergy-be/shared'

const FixedPriceDrawer = ({ onRequesteClose, isOpen }: FixedPriceModalProps) => {
  // Redux store
  const { fixedPrices, inputs } = useStoreSelector((store) => store.customer)
  const { needsGas } = inputs.deliveryAddress
  const dispatch = useStoreDispatch()

  // I18N
  const { t } = useTranslation(['simulationCard', 'validation', 'common'])

  // React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues: { ...fixedPrices }, mode: 'onBlur' })

  /**
   * Handles the submit after validation by React Hook Form
   *
   * @param {SalesPricesFormInputs} data
   */
  const onSubmit = (data: SalesPricesFormInputs) => {
    const { electricity, gas } = data

    // Set the simulation type to fixed amount
    dispatch(
      updateInput({
        group: InputGroupKeys.SIMULATION,
        key: SimulationFieldKeys.CHOSEN_SIMULATION_TYPE,
        value: SIMULATION_TYPES.fixedAmount
      })
    )

    // Update the fixed prices
    dispatch(
      updateFixedPrices({
        electricity: Number(electricity),
        gas: needsGas ? Number(gas ?? 0) : undefined
      })
    )

    // Close the modal
    onRequesteClose()
  }

  return (
    <Drawer isOpen={isOpen} onRequestClose={onRequesteClose} title={t('sales.prices.title', 'Prijzen')}>
      <Form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextField
          type="number"
          step={1}
          {...register('electricity', {
            min: {
              value: needsGas ? 10 : 20,
              message: t('validation:min.number', {
                attribute: t('common:electricity', 'Elektriciteit'),
                min: needsGas ? 10 : 20
              })
            },
            required: t('validation:required')
          })}
          label={`${t('common:electricity', 'Elektriciteit')} ${t('common:inclVat', 'incl. BTW')}`}
          error={errors?.electricity?.message as string}
          grid="full"
        />

        {needsGas && (
          <TextField
            label={`${t('common:gas', 'Gas')} ${t('common:inclVat', 'incl. BTW')}`}
            type="number"
            step={1}
            {...register('gas', {
              min: { value: 10, message: t('validation:min.number', { attribute: t('common:gas', 'Gas'), min: 10 }) },
              required: t('validation:required')
            })}
            error={errors?.gas?.message as string}
            grid="full"
          />
        )}

        <Form.Footer className="mt-auto">
          <Button isFullwidth>{t('salesPrices.save', 'Bewaar')}</Button>
        </Form.Footer>
      </Form>
    </Drawer>
  )
}

export default FixedPriceDrawer
